/**
 * PushNotificationsCard — small opt-in tile the seller (or buyer) can
 * drop anywhere in their dashboard. Toggles OS-level push notifications
 * via the `useWebPush` hook.
 */
import * as React from "react";
import { Bell, BellOff, CheckCircle2, Loader2, ShieldAlert, MonitorSmartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useWebPush } from "@/hooks/useWebPush";

export const PushNotificationsCard: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const { state, enable, disable } = useWebPush();

  const badge = (() => {
    switch (state) {
      case "granted": return { label: "Push on", tone: "emerald", icon: CheckCircle2 };
      case "granted-no-token": return { label: "Permission granted, not registered", tone: "amber", icon: Bell };
      case "denied": return { label: "Blocked by browser", tone: "rose", icon: ShieldAlert };
      case "unsupported": return { label: "This browser doesn't support push", tone: "slate", icon: BellOff };
      case "loading": return { label: "Checking…", tone: "slate", icon: Loader2 };
      default: return { label: "Off", tone: "slate", icon: BellOff };
    }
  })();
  const BadgeIcon = badge.icon;

  return (
    <div
      data-testid="push-notifications-card"
      className={`rounded-2xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-slate-900 ${compact ? "p-3" : "p-4"} space-y-3`}
    >
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 rounded-xl grid place-items-center text-white bg-gradient-to-br ${
          state === "granted" ? "from-emerald-500 to-cyan-500" : "from-orange-500 to-fuchsia-500"
        }`}>
          <MonitorSmartphone className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-neutral-900 dark:text-white">
            Mobile & desktop push
          </div>
          <div className="text-[11px] text-neutral-500 dark:text-slate-400">
            Get buyer chats, new orders, and Sancta commerce updates as OS-level notifications — even when SociBaba is closed.
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2">
        <div className={`inline-flex items-center gap-1.5 text-[11px] rounded-full px-2 py-0.5 border ${
          badge.tone === "emerald" ? "border-emerald-300 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/10"
            : badge.tone === "amber" ? "border-amber-300 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10"
            : badge.tone === "rose"  ? "border-rose-300 text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-500/10"
            : "border-neutral-200 text-neutral-600 dark:text-slate-300 bg-neutral-50 dark:bg-white/5"
        }`}>
          <BadgeIcon className={`h-3 w-3 ${state === "loading" ? "animate-spin" : ""}`} />
          {badge.label}
        </div>

        {state === "granted" ? (
          <Button
            size="sm"
            variant="outline"
            data-testid="push-disable-btn"
            onClick={disable}
          >
            <BellOff className="h-3.5 w-3.5 mr-1.5" /> Turn off
          </Button>
        ) : (
          <Button
            size="sm"
            data-testid="push-enable-btn"
            disabled={state === "unsupported" || state === "denied" || state === "loading"}
            onClick={enable}
          >
            <Bell className="h-3.5 w-3.5 mr-1.5" /> Enable push
          </Button>
        )}
      </div>

      {state === "denied" && (
        <div className="text-[11px] text-rose-600 dark:text-rose-300">
          Browser blocked notifications. Click the padlock icon in the URL bar and re-allow notifications for this site.
        </div>
      )}
    </div>
  );
};

export default PushNotificationsCard;
